/**
 * OTP Send Flow Hook
 *
 * Manages the send → countdown → resend lifecycle for SMS/Email OTP delivery.
 * Extracted from palm-web's MfaChallengeForm for reuse across all apps.
 *
 * @module @puffin-core/mfa-ui/hooks/useOtpSendFlow
 */

import { useState, useCallback } from 'react';
import { useResendCountdown } from './useResendCountdown.js';

/** The send-otp refusal for a number that texted STOP to the sending number (`createMfaOtpRouter`). */
const SMS_OPTED_OUT = 'SMS_OPTED_OUT';

/**
 * The send-otp refusal for a number on the sending COMPANY's own opt-out list — a platform-number code (owner ruling
 * Q-SMS-F3, "Also check company opt-outs"). START to the platform number lifts nothing there, so none is named.
 */
const SMS_OPTED_OUT_BY_COMPANY = 'SMS_OPTED_OUT_BY_COMPANY';

/**
 * The send-otp answer for a code text whose outcome is NOT KNOWN (409, LIFE-SHORTLISTED-001 Q-LS-74): PortPuffin's request
 * may have reached the carrier and its answer was lost, so the text was never sent again. It is SENT, outcome unknown:
 * the code screen opens and a status line says the code may already be on its way — never a failure, which would invite
 * an immediate second code.
 */
const SMS_SEND_IN_DOUBT = 'SMS_SEND_IN_DOUBT';

/**
 * A send inside the server's pace (auth-routes `refuseCodeBeforeItsPace`, D1 errorCode #2b): 429 with the wait the
 * server will hold, `details.retryAfter`, in whole seconds. The server is the authority on its own cooldown, so the
 * countdown resynchronises to it.
 */
const RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED';

/**
 * The wait a paced refusal names, or null when it names none this client can prove (a positive whole number).
 *
 * @param {*} err - What the send threw
 * @returns {?number}
 */
function provenRetryAfterOf(err) {
  if (err?.errorCode !== RATE_LIMIT_EXCEEDED) return null;
  const seconds = err.errorDetails?.retryAfter;
  return Number.isInteger(seconds) && seconds > 0 ? seconds : null;
}

/**
 * A sign-in's send-otp refusals that end the challenge (auth-routes `mfaOtp`, the ONE MFA verify vocabulary): the code
 * cannot be sent, so the person signs in again. Worded from the code (D1 errorCode #2), with login-shell's own words.
 */
const TERMINAL_CHALLENGE_REFUSALS = Object.freeze({
  MFA_CHALLENGE_INVALID: 'error_challenge_invalid',
  MFA_SESSION_EXPIRED: 'error_challenge_expired',
});

/**
 * What the person reads when a send fails. A code refused because their number texted STOP says so, and names the
 * number to text START to (owner ruling 2026-10-06, verbatim: "Keep refusal, explain it (Recommended)"); any other
 * failure reads as it did. The number reads as PortPuffin sent it (E.164): mfa-ui ships in apps that bundle no phone
 * formatter, and pulling one in to pretty-print a single number doubled their vendor chunk (measured 2026-10-06:
 * vendor-misc 361 KB against a 179 KB budget in seven apps).
 *
 * @param {*} err - What the send threw (an auth-client error carries `errorCode` / `errorDetails`)
 * @param {(key: string, params?: Object) => string} t - The MFA strings' translate function
 * @returns {string}
 */
export function describeSendFailure(err, t) {
  if (err?.errorCode === SMS_OPTED_OUT) {
    const number = err.errorDetails?.replyStartTo;
    return number ? t('error_sms_opted_out', { number }) : t('error_sms_opted_out_no_number');
  }
  if (err?.errorCode === SMS_OPTED_OUT_BY_COMPANY) {
    return t('error_sms_opted_out_by_company');
  }
  const terminalLeaf = Object.hasOwn(TERMINAL_CHALLENGE_REFUSALS, err?.errorCode)
    ? TERMINAL_CHALLENGE_REFUSALS[err.errorCode]
    : null;
  if (terminalLeaf) return t(terminalLeaf);
  const retryAfter = provenRetryAfterOf(err);
  if (retryAfter !== null) return t('error_send_too_soon', { seconds: retryAfter });
  return err instanceof Error ? err.message : String(err);
}

/**
 * Hook for managing OTP send/resend with countdown timer.
 *
 * @param {Object} options
 * @param {(method: string) => Promise<void>} options.onSendOtp - Callback to send OTP
 * @param {string} options.method - Current OTP method ('sms' or 'email')
 * @param {number} [options.cooldown=60] - Cooldown in seconds between sends
 * @param {(key: string, params?: Object) => string} options.t - The MFA strings' translate function
 * @returns {{
 *   sent: boolean,
 *   sending: boolean,
 *   sendError: string|null,
 *   sendNotice: string|null,
 *   secondsRemaining: number,
 *   send: () => Promise<void>,
 *   resend: () => Promise<void>,
 *   reset: () => void,
 * }}
 */
export function useOtpSendFlow({ onSendOtp, method, cooldown = 60, t }) {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  // A status line, never an error: the last send may already be on its way (SMS_SEND_IN_DOUBT).
  const [sendNotice, setSendNotice] = useState(null);

  const {
    secondsRemaining,
    start: startCountdown,
    stop: stopCountdown,
  } = useResendCountdown(cooldown);

  const executeSend = useCallback(async () => {
    // `sending` is the anti-double-send guard: it is load-bearing (an OTP send
    // is a rate-limited, cost-bearing side effect) and stays in the dep array so
    // the closure always sees the current value. Do not remove or hoist it.
    if (!onSendOtp || sending) {
      return;
    }
    setSending(true);
    setSendError(null);
    setSendNotice(null);
    try {
      await onSendOtp(method);
      setSent(true);
      startCountdown();
    } catch (err) {
      if (err?.errorCode === SMS_SEND_IN_DOUBT) {
        setSent(true);
        setSendNotice(t('notice_sms_in_doubt'));
        startCountdown();
      } else {
        setSendError(describeSendFailure(err, t));
        const retryAfter = provenRetryAfterOf(err);
        if (retryAfter !== null) startCountdown(retryAfter);
      }
    } finally {
      setSending(false);
    }
  }, [onSendOtp, method, sending, startCountdown, t]);

  const reset = useCallback(() => {
    setSent(false);
    setSending(false);
    setSendError(null);
    setSendNotice(null);
    stopCountdown();
  }, [stopCountdown]);

  return {
    sent,
    sending,
    sendError,
    sendNotice,
    secondsRemaining,
    send: executeSend,
    resend: executeSend,
    reset,
  };
}
